<template>
  <div :style="{ direction: adminLang === 'he' ? 'rtl' : 'ltr', fontFamily: 'system-ui', background: '#f0f4f8', minHeight: '100vh' }">

    <!-- ═══════════════════════════════════════════════════════
         AUTH SCREEN
    ════════════════════════════════════════════════════════════ -->
    <div v-if="!authenticated" class="auth-screen">
      <div class="auth-card">
        <div class="auth-logo">🗺️</div>
        <h2>Admin Panel</h2>
        <p class="text-muted">Connecte-toi avec ton compte Google admin</p>
        <div v-if="authError" class="error-msg">❌ Accès refusé — ce compte n'est pas autorisé</div>
        <button class="btn-google" :disabled="authLoading" @click="signInWithGoogle">
          <span v-if="authLoading">{{ t('connecting') }}</span>
          <span v-else>
            <svg width="18" height="18" viewBox="0 0 48 48" style="vertical-align:middle;margin-right:8px">
              <path fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              <path fill="none" d="M0 0h48v48H0z" />
            </svg>
            {{ t('signInGoogle') }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         GAME SELECTION SCREEN
    ════════════════════════════════════════════════════════════ -->
    <div v-else-if="!navigationName" class="auth-screen">
      <div class="auth-card" style="max-width: 480px">
        <div class="auth-logo">🎮</div>
        <h2>Choose a game</h2>
        <p class="text-muted">Pour créer un nouveau parcours, utilise le tableau de bord (/dashboard).</p>

        <!-- Existing list -->
        <div v-if="loading" class="spinner-wrap">
          <div class="spinner"></div>
        </div>
        <div v-else class="game-list">
          <button v-for="name in allNavGameNames" :key="name" class="game-item" @click="startEditing(name)">
            {{ name }}
          </button>
        </div>

        <button class="topbar-logout mt-3" @click="logout">{{ t('logout') }}</button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         MAIN EDITOR
    ════════════════════════════════════════════════════════════ -->
    <div v-else>

      <!-- Top bar -->
      <div class="topbar">
        <div class="topbar-row topbar-row--title">
          <span class="topbar-game">🗺️ {{ navigationName }}</span>
        </div>
        <div class="topbar-row topbar-row--tabs">
          <button :class="['tab-btn', { active: activeTab === 'points' }]" @click="activeTab = 'points'; loadPoints()">
            {{ t('tabPoints') }} ({{ allPoints.length }})
          </button>
          <button :class="['tab-btn', { active: activeTab === 'settings' }]"
            @click="activeTab = 'settings'; loadSettings()">
            {{ t('tabSettings') }}
          </button>
        </div>
        <div class="topbar-row topbar-row--actions">
          <div v-if="availableLangs.length > 1" class="lang-switcher">
            <button v-for="l in availableLangs" :key="l" :class="['lang-btn', { active: currentLang === l }]"
              @click="switchLang(l)">{{ l.toUpperCase() }}</button>
          </div>
          <div class="admin-lang-switcher">
            <button :class="['admin-lang-btn', adminLang==='fr' ? 'admin-lang-active' : '']" @click="adminLang='fr'">🇫🇷</button>
            <button :class="['admin-lang-btn', adminLang==='en' ? 'admin-lang-active' : '']" @click="adminLang='en'">🇬🇧</button>
            <button :class="['admin-lang-btn', adminLang==='he' ? 'admin-lang-active' : '']" @click="adminLang='he'">🇮🇱</button>
          </div>
          <button class="topbar-back" @click="navigationName = null">{{ t('backToDashboard') }}</button>
          <button class="topbar-logout" @click="logout">{{ t('logout') }}</button>
        </div>
      </div>

      <!-- ─────────────────── TAB: POINTS ─────────────────── -->
      <div v-show="activeTab === 'points'" class="tab-content">
        <div v-if="loading" class="spinner-wrap">
          <div class="spinner"></div>
        </div>
        <div v-else>

          <!-- Points list -->
          <div v-for="point in allPoints" :key="point.pointNumber" class="card">
            <div class="card-header" @click="togglePoint(point)">
              <div class="card-title">
                <span class="point-badge">{{ point.pointNumber }}</span>
                <div class="point-meta">
                  <div class="point-meta-row">
                    <span class="point-label">{{ t('screen') }}</span>
                    <span class="point-screen-badge">{{ point.navigationScreen || 'imagesList' }}</span>
                  </div>
                  <div class="point-meta-row">
                    <span class="point-label">{{ t('info') }}</span>
                    <span v-if="point.pointInformation" class="point-info-badge text">{{ t('labelText') }}</span>
                    <span v-if="point.videoUrl" class="point-info-badge video">{{ t('labelVideo') }}</span>
                    <span v-if="point.audioUrl" class="point-info-badge audio">{{ t('labelAudio') }}</span>
                    <span v-if="point.withAR" class="point-info-badge ar">{{ t('labelAR') }}</span>
                    <span v-if="point.withPhone" class="point-info-badge phone">{{ t('labelPhone') }}</span>
                    <span v-if="!point.pointInformation && !point.videoUrl && !point.audioUrl && !point.withAR && !point.withPhone"
                      class="point-info-badge empty">—</span>
                  </div>
                  <div class="point-meta-row">
                    <span class="point-label">{{ t('exercise') }}</span>
                    <span class="point-picked-badge">{{ point.picked || 'questions' }}</span>
                    <span class="point-q-badge">{{ questionsForPoint(point.pointNumber).length }} Q</span>
                  </div>
                  <div v-if="point.navigationScreen === 'missingWord'" class="point-meta-row">
                    <span class="point-label">{{ t('missingWord') }}</span>
                    <span class="point-missing-word">{{ point.missingWord || '—' }}</span>
                  </div>
                </div>
              </div>
              <div class="card-actions">
                <span v-if="!point.editMode" class="chevron">{{ point._open ? '▲' : '▼' }}</span>
                <button v-if="point.editMode" class="btn-save" @click.stop="savePoint(point)">{{ t('save') }}</button>
                <button v-if="point.editMode" class="btn-cancel" @click.stop="cancelEdit(point)">✕</button>
                <button v-if="!point.editMode && point._open" class="btn-edit" @click.stop="point.editMode = true">{{ t('edit') }}</button>
              </div>
            </div>

            <!-- READ VIEW -->
            <div v-if="point._open && !point.editMode" class="card-body">

              <!-- IMAGE -->
              <div v-if="point.imageUrl" class="read-section">
                <div class="read-section-title">🖼️ {{ t('image') }}</div>
                <img :src="point.imageUrl" class="point-thumb" alt="" />
              </div>

              <!-- SCREEN -->
              <div class="read-section">
                <div class="read-section-title">{{ t('sectionScreen') }}</div>
                <span class="read-value">{{ point.navigationScreen || 'imagesList' }}</span>
                <div v-if="point.navigationScreen === 'missingWord'" class="read-field mt-1">
                  <span class="label">{{ t('missingWordReadLabel') }}</span>
                  <span class="read-value">{{ point.missingWord || '—' }}</span>
                </div>
              </div>

              <!-- INFORMATION -->
              <div class="read-section">
                <div class="read-section-title">{{ t('sectionInfo') }}</div>
                <div v-if="point.pointInformation" class="read-field">
                  <span class="label">{{ t('labelText') }}</span>
                  <p class="info-text">{{ point.pointInformation }}</p>
                </div>
                <div v-if="point.audioUrl" class="read-field">
                  <span class="label">{{ t('labelAudio') }}</span>
                  <span class="read-value">{{ point.audioUrl }}</span>
                </div>
                <div v-if="point.videoUrl" class="read-field">
                  <span class="label">{{ t('labelVideoId') }}</span>
                  <span class="read-value">{{ point.videoUrl }}</span>
                </div>
                <div v-if="point.withAR" class="read-field">
                  <span class="label">{{ t('labelAR') }}</span>
                  <span class="read-value">🧭 {{ arDirectionLabel(point.arDirection) }} — {{ point.arVideoUrl ? '✅ GIF set' : '⚠️ GIF missing' }}</span>
                </div>
                <div v-if="point.withPhone" class="read-field">
                  <span class="label">{{ t('labelPhone') }}</span>
                  <span class="read-value">{{ point.callerName || '—' }} · {{ point.phoneAudioUrl || t('noAudio') }}</span>
                </div>
                <div v-if="!point.pointInformation && !point.audioUrl && !point.videoUrl && !point.withAR && !point.withPhone" class="read-value muted">—
                </div>
              </div>

              <!-- EXERCICE -->
              <div class="read-section">
                <div class="read-section-title">{{ t('sectionExercise') }}</div>
                <span class="read-value">{{ point.picked || 'questions' }}</span>
                <div v-if="point.picked === 'puzzle'" class="mt-2">
                  <img v-if="point.puzzleImageBase64 || point.puzzleImageUrl"
                    :src="point.puzzleImageBase64 || point.puzzleImageUrl" class="puzzle-thumb" alt="puzzle" />
                  <span v-else class="muted">{{ t('noPuzzleImage') }}</span>
                </div>
                <div v-if="point.picked === 'memory'" class="mt-2 muted">
                  {{ t('memoryUsesPointImages') }}
                </div>
                <div v-if="point.picked === 'multiplematch'" class="mm-read-grid mt-2">
                  <div v-for="n in 4" :key="n" class="mm-pair-view">
                    <img v-if="point['itemImage' + n]" :src="point['itemImage' + n]" class="mm-thumb" alt="" />
                    <div v-else class="mm-empty">—</div>
                    <span class="mm-desc">{{ point['description' + n] || '—' }}</span>
                  </div>
                </div>
              </div>

              <!-- LAT / LNG -->
              <div class="read-section">
                <div class="read-section-title">{{ t('coordinates') }}</div>
                <div class="read-grid">
                  <div class="read-item"><span class="label">{{ t('lat') }}</span><span>{{ point.lat || '—' }}</span></div>
                  <div class="read-item"><span class="label">{{ t('lng') }}</span><span>{{ point.lng || '—' }}</span></div>
                </div>
                <div v-if="point.waypoints && point.waypoints.length" class="mt-1">
                  <div v-for="(wp, i) in point.waypoints" :key="i" class="read-value">{{ t('waypoint') }} {{ i + 1 }}: {{ wp.lat }}, {{ wp.lng }}</div>
                </div>
              </div>

            </div>

            <!-- EDIT VIEW -->
            <div v-if="point._open && point.editMode" class="card-body edit-form">
              <!-- 1. Point number -->
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('pointNumber') }}</label>
                  <input v-model.number="point.pointNumber" type="number" class="form-input-sm" />
                </div>
              </div>
              <!-- 2. Image -->
              <div class="field-group">
                <label class="field-label">{{ t('image') }}</label>
                <div v-if="point.imagePreview || point.imageUrl" class="thumb-row">
                  <img :src="point.imagePreview || point.imageUrl" class="point-thumb mt-2 mb-2" alt="" />
                  <button class="btn-edit-photo" @click="openPhotoEditor(point)">{{ t('editPhoto') }}</button>
                </div>
                <label class="file-btn">
                  {{ point.imageUrl ? t('changeImage') : t('chooseImage') }}
                  <input type="file" accept="image/*" style="display:none" @change="(e) => onImagePicked(e, point)" />
                </label>
              </div>
              <!-- 3. Navigation screen -->
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('navigationScreen') }}</label>
                  <select v-model="point.navigationScreen" class="form-input-sm">
                    <option value="imagesList">{{ t('screenImagesList') }}</option>
                    <option value="dynamicMap">{{ t('screenDynamicMap') }}</option>
                    <option value="carousel">{{ t('screenCarousel') }}</option>
                    <option value="missingWord">{{ t('screenMissingWord') }}</option>
                  </select>
                </div>
              </div>
              <!-- 4. Mot manquant (seulement si écran missingWord) -->
              <div v-if="point.navigationScreen === 'missingWord'" class="field-group">
                <label class="field-label">{{ t('missingWordLabel') }}</label>
                <input v-model="point.missingWord" class="form-input-sm" :placeholder="t('missingWordVisible')" />
              </div>
              <!-- Information / text fields with checkboxes -->
              <div class="field-row checkbox-row">
                <label class="check-label">
                  <input type="checkbox"
                    :checked="point._showText !== undefined ? point._showText : !!point.pointInformation"
                    @change="e => { point._showText = e.target.checked; $forceUpdate(); }" /> {{ t('labelText') }}
                </label>
                <label class="check-label">
                  <input type="checkbox" :checked="point._showAudio !== undefined ? point._showAudio : !!point.audioUrl"
                    @change="e => { point._showAudio = e.target.checked; $forceUpdate(); }" /> {{ t('labelAudio') }}
                </label>
                <label class="check-label">
                  <input type="checkbox" v-model="point.withVideo"
                    @change="e => { if (!e.target.checked) { point.videoUrl = ''; $forceUpdate(); } }" /> {{ t('labelVideo') }}
                </label>
                <label class="check-label">
                  <input type="checkbox" v-model="point.withAR" /> {{ t('labelAR') }}
                </label>
                <label class="check-label">
                  <input type="checkbox"
                    :checked="point._showPhone !== undefined ? point._showPhone : !!point.withPhone"
                    @change="e => { point._showPhone = e.target.checked; point.withPhone = e.target.checked; if (!e.target.checked) { point.phoneAudioUrl = ''; point.callerName = ''; } $forceUpdate(); }" /> {{ t('labelPhone') }}
                </label>
              </div>
              <div v-if="point._showPhone !== undefined ? point._showPhone : !!point.withPhone" class="field-group">
                <label class="field-label">{{ t('callerName') }}</label>
                <input v-model="point.callerName" class="form-input-sm" :placeholder="t('callerNamePlaceholder')" />
                <label class="field-label mt-1">{{ t('phoneAudioUrl') }}</label>
                <input v-model="point.phoneAudioUrl" class="form-input-sm" placeholder="https://..." />
              </div>
              <div v-if="point._showText !== undefined ? point._showText : !!point.pointInformation"
                class="field-group">
                <label class="field-label">{{ t('pointInformation') }}</label>
                <textarea v-model="point.pointInformation" class="form-textarea" rows="4"></textarea>
              </div>
              <div v-if="point._showAudio !== undefined ? point._showAudio : !!point.audioUrl" class="field-group">
                <label class="field-label">{{ t('audioUrl') }}</label>
                <input v-model="point.audioUrl" class="form-input-sm" placeholder="https://..." />
              </div>
              <div v-if="point.withVideo" class="field-group">
                <label class="field-label">{{ t('youtubeUrl') }} <span v-if="point.withVerticalVideo" style="color:#f59e0b;font-size:11px">{{ t('shortsDetected') }}</span></label>
                <input :value="point.videoUrl" class="form-input-sm" placeholder="https://youtube.com/shorts/... or dQw4w9WgXcQ"
                  @input="e => parseVideoInput(e.target.value, point)" />
                <div v-if="point.withVideo && !point.videoUrl" class="field-warn">
                  {{ t('youtubeWarning') }}
                </div>
              </div>
              <div v-if="point.withAR" class="field-group">
                <label class="field-label mt-1">{{ t('arDirection') }}</label>
                <div class="ar-dir-toggle mt-1">
                  <button :class="['ar-dir-btn', !point.arDirectionExact ? 'active' : '']" @click="point.arDirectionExact = false">{{ t('arPreset') }}</button>
                  <button :class="['ar-dir-btn', point.arDirectionExact ? 'active' : '']" @click="point.arDirectionExact = true">{{ t('arExactDeg') }}</button>
                </div>
                <select v-if="!point.arDirectionExact" v-model.number="point.arDirection" class="form-input-sm mt-1">
                  <option :value="0">{{ t('arNorth') }}</option>
                  <option :value="45">{{ t('arNE') }}</option>
                  <option :value="90">{{ t('arEast') }}</option>
                  <option :value="135">{{ t('arSE') }}</option>
                  <option :value="180">{{ t('arSouth') }}</option>
                  <option :value="225">{{ t('arSW') }}</option>
                  <option :value="270">{{ t('arWest') }}</option>
                  <option :value="315">{{ t('arNW') }}</option>
                </select>
                <div v-else class="ar-deg-input mt-1">
                  <input v-model.number="point.arDirection" type="number" min="0" max="359" class="form-input-sm" placeholder="0–359" />
                  <span class="ar-deg-unit">°</span>
                </div>
                <label class="field-label mt-2">{{ t('arEffectUrl') }}</label>
                <input v-model="point.arVideoUrl" class="form-input-sm" placeholder="https://..." />
                <label class="field-label mt-2">{{ t('arSearchWord') }}</label>
                <input v-model="point.arSearchWord" class="form-input-sm" :placeholder="t('arSearchWordPlaceholder')" />
                <div class="field-hint">{{ t('arSearchWordHint') }}</div>
                <div class="ar-gif-options">
                  <div class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arGifCount') }}</label>
                    <select v-model.number="point.arGifCount" class="form-input-sm">
                      <option :value="1">{{ t('arGif1') }}</option>
                      <option :value="2">{{ t('arGif2') }}</option>
                      <option :value="3">3</option>
                    </select>
                  </div>
                  <div v-if="point.arGifCount > 1" class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arSpacing') }}</label>
                    <input v-model.number="point.arGifSpacing" type="number" class="form-input-sm" placeholder="0" />
                    <div class="field-hint">{{ t('arSpacingHint') }}</div>
                  </div>
                </div>
                <label class="field-label mt-2">{{ t('arGrowAnimation') }}</label>
                <div class="ar-anim-toggle">
                  <button :class="['ar-dir-btn', point.arGrowAnimation !== false ? 'active' : '']" @click="point.arGrowAnimation = true">{{ t('arGrow') }}</button>
                  <button :class="['ar-dir-btn', point.arGrowAnimation === false ? 'active' : '']" @click="point.arGrowAnimation = false">{{ t('arAppear') }}</button>
                </div>
              </div>
              <!-- 5. Exercise -->
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('exerciseType') }}</label>
                  <select v-model="point.picked" class="form-input-sm">
                    <option value="questions">{{ t('exerciseQuestions') }}</option>
                    <option value="puzzle">{{ t('exercisePuzzle') }}</option>
                    <option value="multiplematch">{{ t('exerciseMultipleMatch') }}</option>
                    <option value="memory">{{ t('exerciseMemory') }}</option>
                    <option value="justefaux">{{ t('exerciseJusteFaux') }}</option>
                    <option value="motManquant">{{ t('exerciseMotManquant') }}</option>
                    <option value="rushhour">{{ t('exerciseRushHour') }}</option>
                    <option value="signcard">{{ t('exerciseSignCard') }}</option>
                  </select>
                </div>
              </div>
              <div v-if="point.picked === 'signcard'" class="field-group mt-2">
                <div class="section-divider">{{ t('signCardTitle') }}</div>
                <label class="field-label">{{ t('signCardImage') }}</label>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="(e) => onSignCardImagePicked(e, point)" />
                <div v-if="point.signCardImageUrl || point.signCardImageBase64" class="sc-admin-preview-wrap mt-2">
                  <img :src="point.signCardImageUrl || point.signCardImageBase64" class="sc-admin-img" />
                  <div class="sc-admin-zone" :style="signCardZoneStyle(point)">{{ t('signCardZone') }}</div>
                </div>
                <label class="field-label mt-2">{{ t('signCardHeight') }} {{ point.signCardSigHeightPct || 20 }}%</label>
                <input type="range" min="5" max="50" step="5" class="sc-range mt-1"
                  :value="point.signCardSigHeightPct || 20"
                  @input="point.signCardSigHeightPct = +$event.target.value; $forceUpdate()" />
                <label class="field-label mt-2">{{ t('signCardVAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="v in [{k:'top',tKey:'alignTop'},{k:'center',tKey:'alignCenter'},{k:'bottom',tKey:'alignBottom'}]" :key="v.k"
                    :class="['sc-align-btn', (point.signCardSigVAlign || 'bottom') === v.k ? 'sc-align-active' : '']"
                    @click="point.signCardSigVAlign = v.k; $forceUpdate()">{{ t(v.tKey) }}</button>
                </div>
                <label class="field-label mt-2">{{ t('signCardHAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="h in [{k:'left',tKey:'alignLeft'},{k:'center',tKey:'alignCenter'},{k:'right',tKey:'alignRight'}]" :key="h.k"
                    :class="['sc-align-btn', (point.signCardSigHAlign || 'center') === h.k ? 'sc-align-active' : '']"
                    @click="point.signCardSigHAlign = h.k; $forceUpdate()">{{ t(h.tKey) }}</button>
                </div>
              </div>
              <div v-if="point.picked === 'rushhour'" class="field-group mt-2">
                <div class="section-divider">{{ t('rushHourTitle') }}</div>
                <RushHourEditor v-model="point.rushHourVehicles" />
              </div>
              <div v-if="point.picked === 'motManquant'" class="field-group mt-2">
                <div class="section-divider">{{ t('exerciseMotManquant') }}</div>
                <div class="upload-hint">{{ t('mmqHint') }} <strong>{mot}</strong></div>
                <textarea v-model="point.motManquantText" class="form-textarea" rows="5"
                  :placeholder="t('mmqPlaceholder')" />
                <div v-if="point.motManquantText" class="field-hint">
                  {{ (point.motManquantText.match(/\{[^}]+\}/g) || []).length }} {{ t('mmqHoles') }} :
                  {{ (point.motManquantText.match(/\{([^}]+)\}/g) || []).map(m => m.slice(1,-1)).join(', ') }}
                </div>
                <label class="field-label mt-2">{{ t('mmqMode') }}</label>
                <div class="mm-mode-toggle">
                  <button :class="['mm-mode-btn', (point.motManquantMode || 'visible') === 'visible' ? 'mm-mode-active' : '']"
                    @click="point.motManquantMode = 'visible'; $forceUpdate()">{{ t('mmqVisible') }}</button>
                  <button :class="['mm-mode-btn', point.motManquantMode === 'hidden' ? 'mm-mode-active' : '']"
                    @click="point.motManquantMode = 'hidden'; $forceUpdate()">{{ t('mmqHidden') }}</button>
                </div>
              </div>
              <div v-if="point.picked === 'justefaux'" class="field-group mt-2">
                <div class="section-divider">{{ t('jfTitle') }}</div>
                <label class="field-label">{{ t('jfQuestion') }}</label>
                <input v-model="point.justeFauxQuestion" class="form-input-sm" :placeholder="t('jfQuestionPlaceholder')" />
                <label class="field-label mt-2">{{ t('jfOptions') }}</label>
                <div v-for="(item, idx) in normalizeJFItems(point.justeFauxItems)" :key="idx" class="jf-admin-row">
                  <input v-model="item.label" class="form-input-sm jf-label-input" placeholder="Option…" />
                  <button :class="['jf-toggle-btn', item.correct === true ? 'jf-active-vrai' : '']" @click="item.correct = true; $forceUpdate()">✓</button>
                  <button :class="['jf-toggle-btn', item.correct !== true ? 'jf-active-faux' : '']" @click="item.correct = false; $forceUpdate()">✗</button>
                  <button class="jf-remove-btn" @click="removeJusteFauxItem(point, idx)">🗑</button>
                </div>
                <button class="btn-ghost mt-2" @click="addJusteFauxItem(point)">{{ t('jfAddOption') }}</button>
              </div>
              <div v-if="point.picked === 'puzzle'" class="field-group">
                <label class="field-label">{{ t('puzzleImage') }}</label>
                <div class="upload-hint">{{ t('puzzleImageHint') }}</div>
                <input type="file" accept="image/*" class="form-input-sm mt-1"
                  @change="(e) => onPuzzleImagePicked(e, point)" />
                <img v-if="point.puzzleImageUrl" :src="point.puzzleImageUrl" class="puzzle-thumb mt-2" alt="" />
              </div>
              <div v-if="point.picked === 'multiplematch'">
                <div class="section-divider">{{ t('mmTitle') }}</div>
                <div class="upload-hint">{{ t('mmHint') }}</div>
                <div v-for="n in 4" :key="n" class="mm-pair-edit">
                  <div class="mm-pair-header">{{ t('mmPair') }} {{ n }}</div>
                  <div class="mm-pair-row">
                    <div class="mm-img-col">
                      <input type="file" accept="image/*" class="form-input-sm"
                        @change="e => onMmImagePicked(e, point, n)" />
                      <img v-if="point['itemImage' + n]" :src="point['itemImage' + n]" class="mm-thumb mt-1" alt="" />
                      <button v-if="point['itemImage' + n]" class="btn-edit-photo mt-1" @click="openPhotoEditor(point, 'itemImage' + n)">{{ t('editPhoto') }}</button>
                    </div>
                    <div class="mm-label-col">
                      <label class="field-label">{{ t('mmLabel') }}</label>
                      <input v-model="point['description' + n]" class="form-input-sm"
                        :placeholder="t('mmLabelPlaceholder') + ' ' + n" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="point.picked === 'memory'" class="field-group mt-2">
                <div class="section-divider">{{ t('memoryImages') }}</div>
                <div class="upload-hint">{{ t('memoryImagesHint') }}</div>
                <label class="file-btn mt-1">
                  {{ t('addImage') }} ({{ (point.memoryImages || []).length }}/6)
                  <input v-if="(point.memoryImages || []).length < 6" type="file" accept="image/*" style="display:none" @change="e => onMemoryImagePicked(e, point)" />
                </label>
                <div class="mem-img-preview-row">
                  <div v-for="(img, i) in (point.memoryImages || [])" :key="i" class="mem-img-preview">
                    <img :src="img" alt="" />
                    <button class="mem-img-remove" @click="point.memoryImages.splice(i, 1)">✕</button>
                  </div>
                </div>
              </div>
              <!-- 6. Lat / Lng -->
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('latitude') }}</label>
                  <input v-model="point.lat" class="form-input-sm" placeholder="32.657" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('longitude') }}</label>
                  <input v-model="point.lng" class="form-input-sm" placeholder="35.417" />
                </div>
                <button class="btn-map-pick" @click="openMapPicker(point, 'lat', 'lng')">📍</button>
              </div>
              <!-- Waypoints -->
              <div class="section-divider">{{ t('waypoints') }}</div>
              <div v-for="(wp, i) in point.waypoints || []" :key="i" class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('waypoint') }} {{ i + 1 }} {{ t('lat') }}</label>
                  <input v-model="wp.lat" class="form-input-sm" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('waypoint') }} {{ i + 1 }} {{ t('lng') }}</label>
                  <input v-model="wp.lng" class="form-input-sm" />
                </div>
                <button class="btn-map-pick" @click="openMapPicker(wp, 'lat', 'lng')">📍</button>
              </div>
              <button class="btn-ghost mt-2" @click="addWaypoint(point)">{{ t('addWaypoint') }}</button>
            </div>

            <!-- ── Questions de ce point ── -->
            <div v-if="point._open" class="point-questions-section">
              <div class="pqs-header">{{ t('questionsTitle') }} ({{ questionsForPoint(point.pointNumber).length }})</div>

              <div v-for="q in questionsForPoint(point.pointNumber)" :key="q.key" class="pq-card">
                <div class="pq-card-header" @click="q._open = !q._open; $forceUpdate()">
                  <span class="pq-text">{{ q.data.question }}</span>
                  <div style="display:flex;gap:4px;flex-shrink:0">
                    <button v-if="!q.editMode" class="btn-edit"
                      @click.stop="q.editMode = true; $forceUpdate()">✏️</button>
                    <button v-if="q.editMode" class="btn-save" @click.stop="saveQuestion(q)">💾</button>
                    <button v-if="q.editMode" class="btn-cancel"
                      @click.stop="q.editMode = false; $forceUpdate()">✕</button>
                    <span v-if="!q.editMode" class="chevron">{{ q._open ? '▲' : '▼' }}</span>
                  </div>
                </div>
                <div v-if="q._open && !q.editMode" class="pq-body">
                  <div class="answer correct">✅ {{ q.data.correctAnswer }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer1 }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer2 }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer3 }}</div>
                </div>
                <div v-if="q._open && q.editMode" class="pq-body edit-form">
                  <div class="field-group"><label class="field-label">{{ t('jfQuestion') }}</label>
                    <textarea v-model="q.data.question" class="form-textarea" rows="2"></textarea>
                  </div>
                  <div class="field-group"><label class="field-label">{{ t('correctAnswer') }}</label>
                    <input v-model="q.data.correctAnswer" class="form-input-sm correct-input" />
                  </div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer1') }}</label>
                    <input v-model="q.data.wrongAnswer1" class="form-input-sm" />
                  </div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer2') }}</label>
                    <input v-model="q.data.wrongAnswer2" class="form-input-sm" />
                  </div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer3') }}</label>
                    <input v-model="q.data.wrongAnswer3" class="form-input-sm" />
                  </div>
                </div>
              </div>

              <!-- Formulaire nouvelle question -->
              <div v-if="point._addingQuestion" class="pq-card pq-new">
                <div class="field-group"><label class="field-label">{{ t('jfQuestion') }} *</label>
                  <textarea v-model="point._nq.question" class="form-textarea" rows="2"></textarea>
                </div>
                <div class="field-group"><label class="field-label">{{ t('correctAnswer') }} *</label>
                  <input v-model="point._nq.correctAnswer" class="form-input-sm correct-input" />
                </div>
                <div class="field-group"><label class="field-label">{{ t('wrongAnswer1') }}</label>
                  <input v-model="point._nq.wrongAnswer1" class="form-input-sm" />
                </div>
                <div class="field-group"><label class="field-label">{{ t('wrongAnswer2') }}</label>
                  <input v-model="point._nq.wrongAnswer2" class="form-input-sm" />
                </div>
                <div class="field-group"><label class="field-label">{{ t('wrongAnswer3') }}</label>
                  <input v-model="point._nq.wrongAnswer3" class="form-input-sm" />
                </div>
                <div class="row-buttons mt-3">
                  <button class="btn-secondary" @click="point._addingQuestion = false; $forceUpdate()">{{ t('cancel') }}</button>
                  <button class="btn-success" @click="submitQuestionForPoint(point)">{{ t('save') }}</button>
                </div>
              </div>
              <button v-else class="pqs-add-btn" @click="point._addingQuestion = true; $forceUpdate()">{{ t('addQuestion') }}</button>
            </div>

          </div><!-- /card -->

          <!-- Add point button -->
          <div class="card add-card" @click="openNewPointForm">
            {{ t('addPoint') }}
          </div>

          <!-- NEW POINT FORM -->
          <div v-if="newPoint" class="card new-form-card">
            <div class="card-header">
              <span class="card-title">{{ t('newPoint') }}</span>
              <button class="btn-cancel" @click="newPoint = false; resetNewPoint()">✕</button>
            </div>
            <div class="card-body edit-form">
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('pointNumber') }} *</label>
                  <input v-model.number="np.pointNumber" type="number" class="form-input-sm" />
                </div>
              </div>
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('latitude') }} *</label>
                  <input v-model="np.lat" class="form-input-sm" placeholder="32.657" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('longitude') }} *</label>
                  <input v-model="np.lng" class="form-input-sm" placeholder="35.417" />
                </div>
                <button class="btn-map-pick" @click="openMapPicker(np, 'lat', 'lng')">📍</button>
              </div>
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('navigationScreen') }}</label>
                  <select v-model="np.navigationScreen" class="form-input-sm">
                    <option value="imagesList">{{ t('screenImagesList') }}</option>
                    <option value="dynamicMap">{{ t('screenDynamicMap') }}</option>
                    <option value="carousel">{{ t('screenCarousel') }}</option>
                    <option value="missingWord">{{ t('screenMissingWord') }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('exerciseType') }}</label>
                  <select v-model="np.picked" class="form-input-sm">
                    <option value="questions">{{ t('exerciseQuestions') }}</option>
                    <option value="puzzle">{{ t('exercisePuzzle') }}</option>
                    <option value="multiplematch">{{ t('exerciseMultipleMatch') }}</option>
                    <option value="memory">{{ t('exerciseMemory') }}</option>
                    <option value="justefaux">{{ t('exerciseJusteFaux') }}</option>
                    <option value="motManquant">{{ t('exerciseMotManquant') }}</option>
                    <option value="rushhour">{{ t('exerciseRushHour') }}</option>
                    <option value="signcard">{{ t('exerciseSignCard') }}</option>
                  </select>
                </div>
              </div>
              <div v-if="np.picked === 'signcard'" class="field-group mt-2">
                <div class="section-divider">{{ t('signCardTitle') }}</div>
                <label class="field-label">{{ t('signCardImage') }}</label>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="onNewSignCardImagePicked" />
                <div v-if="np.signCardImagePreview" class="sc-admin-preview-wrap mt-2">
                  <img :src="np.signCardImagePreview" class="sc-admin-img" />
                  <div class="sc-admin-zone" :style="signCardZoneStyle(np)">{{ t('signCardZone') }}</div>
                </div>
                <label class="field-label mt-2">{{ t('signCardHeight') }} {{ np.signCardSigHeightPct || 20 }}%</label>
                <input type="range" min="5" max="50" step="5" class="sc-range mt-1"
                  :value="np.signCardSigHeightPct || 20"
                  @input="np.signCardSigHeightPct = +$event.target.value; $forceUpdate()" />
                <label class="field-label mt-2">{{ t('signCardVAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="v in [{k:'top',tKey:'alignTop'},{k:'center',tKey:'alignCenter'},{k:'bottom',tKey:'alignBottom'}]" :key="v.k"
                    :class="['sc-align-btn', (np.signCardSigVAlign || 'bottom') === v.k ? 'sc-align-active' : '']"
                    @click="np.signCardSigVAlign = v.k; $forceUpdate()">{{ t(v.tKey) }}</button>
                </div>
                <label class="field-label mt-2">{{ t('signCardHAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="h in [{k:'left',tKey:'alignLeft'},{k:'center',tKey:'alignCenter'},{k:'right',tKey:'alignRight'}]" :key="h.k"
                    :class="['sc-align-btn', (np.signCardSigHAlign || 'center') === h.k ? 'sc-align-active' : '']"
                    @click="np.signCardSigHAlign = h.k; $forceUpdate()">{{ t(h.tKey) }}</button>
                </div>
              </div>
              <div v-if="np.picked === 'rushhour'" class="field-group mt-2">
                <div class="section-divider">{{ t('rushHourTitle') }}</div>
                <RushHourEditor v-model="np.rushHourVehicles" />
              </div>
              <div v-if="np.picked === 'puzzle'" class="field-group">
                <label class="field-label">{{ t('puzzleImage') }}</label>
                <div class="upload-hint">{{ t('puzzleImageHint') }}</div>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="onNewPointPuzzleImagePicked" />
                <img v-if="np.puzzleImagePreview" :src="np.puzzleImagePreview" class="puzzle-thumb mt-2" alt="" />
              </div>
              <div v-if="np.picked === 'multiplematch'">
                <div class="section-divider">{{ t('mmTitle') }}</div>
                <div class="upload-hint">{{ t('mmHint') }}</div>
                <div v-for="n in 4" :key="n" class="mm-pair-edit">
                  <div class="mm-pair-header">{{ t('mmPair') }} {{ n }}</div>
                  <div class="mm-pair-row">
                    <div class="mm-img-col">
                      <input type="file" accept="image/*" class="form-input-sm"
                        @change="e => onNewPointMmImagePicked(e, n)" />
                      <img v-if="np['itemImage' + n]" :src="np['itemImage' + n]" class="mm-thumb mt-1" alt="" />
                      <button v-if="np['itemImage' + n]" class="btn-edit-photo mt-1" @click="openPhotoEditor(np, 'itemImage' + n)">{{ t('editPhoto') }}</button>
                    </div>
                    <div class="mm-label-col">
                      <label class="field-label">{{ t('mmLabel') }}</label>
                      <input v-model="np['description' + n]" class="form-input-sm"
                        :placeholder="t('mmLabelPlaceholder') + ' ' + n" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="np.picked === 'memory'" class="field-group mt-2">
                <div class="section-divider">{{ t('memoryImages') }}</div>
                <div class="upload-hint">{{ t('memoryImagesHint') }}</div>
                <label class="file-btn mt-1">
                  {{ t('addImage') }} ({{ (np.memoryImages || []).length }}/6)
                  <input v-if="(np.memoryImages || []).length < 6" type="file" accept="image/*" style="display:none" @change="e => onMemoryImagePicked(e, np)" />
                </label>
                <div class="mem-img-preview-row">
                  <div v-for="(img, i) in (np.memoryImages || [])" :key="i" class="mem-img-preview">
                    <img :src="img" alt="" />
                    <button class="mem-img-remove" @click="np.memoryImages.splice(i, 1)">✕</button>
                  </div>
                </div>
              </div>
              <div v-if="np.picked === 'motManquant'" class="field-group mt-2">
                <div class="section-divider">{{ t('exerciseMotManquant') }}</div>
                <div class="upload-hint">{{ t('mmqHint') }} <strong>{mot}</strong></div>
                <textarea v-model="np.motManquantText" class="form-textarea" rows="5"
                  :placeholder="t('mmqPlaceholder')" />
                <div v-if="np.motManquantText" class="field-hint">
                  {{ (np.motManquantText.match(/\{[^}]+\}/g) || []).length }} {{ t('mmqHoles') }} :
                  {{ (np.motManquantText.match(/\{([^}]+)\}/g) || []).map(m => m.slice(1,-1)).join(', ') }}
                </div>
                <label class="field-label mt-2">{{ t('mmqMode') }}</label>
                <div class="mm-mode-toggle">
                  <button :class="['mm-mode-btn', (np.motManquantMode || 'visible') === 'visible' ? 'mm-mode-active' : '']"
                    @click="np.motManquantMode = 'visible'; $forceUpdate()">{{ t('mmqVisible') }}</button>
                  <button :class="['mm-mode-btn', np.motManquantMode === 'hidden' ? 'mm-mode-active' : '']"
                    @click="np.motManquantMode = 'hidden'; $forceUpdate()">{{ t('mmqHidden') }}</button>
                </div>
              </div>
              <div v-if="np.picked === 'justefaux'" class="field-group mt-2">
                <div class="section-divider">{{ t('jfTitle') }}</div>
                <label class="field-label">{{ t('jfQuestion') }}</label>
                <input v-model="np.justeFauxQuestion" class="form-input-sm" :placeholder="t('jfQuestionPlaceholder')" />
                <label class="field-label mt-2">{{ t('jfOptions') }}</label>
                <div v-for="(item, idx) in normalizeJFItems(np.justeFauxItems)" :key="idx" class="jf-admin-row">
                  <input v-model="item.label" class="form-input-sm jf-label-input" :placeholder="t('jfOptionPlaceholder')" />
                  <button :class="['jf-toggle-btn', item.correct === true ? 'jf-active-vrai' : '']" @click="item.correct = true; $forceUpdate()">✓</button>
                  <button :class="['jf-toggle-btn', item.correct !== true ? 'jf-active-faux' : '']" @click="item.correct = false; $forceUpdate()">✗</button>
                  <button class="jf-remove-btn" @click="removeJusteFauxItem(np, idx)">🗑</button>
                </div>
                <button class="btn-ghost mt-2" @click="addJusteFauxItem(np)">{{ t('jfAddOption') }}</button>
              </div>
              <div class="field-group">
                <label class="field-label">{{ t('missingWordLabel') }}</label>
                <input v-model="np.missingWord" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">{{ t('pointInformation') }}</label>
                <textarea v-model="np.pointInformation" class="form-textarea" rows="4"></textarea>
              </div>
              <div class="field-group">
                <label class="field-label">{{ t('audioUrl') }}</label>
                <input v-model="np.audioUrl" class="form-input-sm" />
              </div>
              <label class="check-label">
                <input type="checkbox" v-model="np.withVideo" /> {{ t('labelVideo') }}
              </label>
              <label class="check-label">
                <input type="checkbox" v-model="np.withAR" /> {{ t('labelAR') }}
              </label>
              <label class="check-label">
                <input type="checkbox" v-model="np.withPhone" /> {{ t('labelPhone') }}
              </label>
              <div v-if="np.withPhone" class="field-group mt-2">
                <label class="field-label">{{ t('callerName') }}</label>
                <input v-model="np.callerName" class="form-input-sm" :placeholder="t('callerNamePlaceholder')" />
                <label class="field-label mt-1">{{ t('phoneAudioUrl') }}</label>
                <input v-model="np.phoneAudioUrl" class="form-input-sm" placeholder="https://..." />
              </div>
              <div v-if="np.withVideo" class="field-group mt-2">
                <label class="field-label">{{ t('youtubeUrl') }}</label>
                <input v-model="np.videoUrl" class="form-input-sm" />
              </div>
              <div v-if="np.withAR" class="field-group mt-2">
                <label class="field-label mt-1">{{ t('arDirection') }}</label>
                <div class="ar-dir-toggle mt-1">
                  <button :class="['ar-dir-btn', !np.arDirectionExact ? 'active' : '']" @click="np.arDirectionExact = false">{{ t('arPreset') }}</button>
                  <button :class="['ar-dir-btn', np.arDirectionExact ? 'active' : '']" @click="np.arDirectionExact = true">{{ t('arExactDeg') }}</button>
                </div>
                <select v-if="!np.arDirectionExact" v-model.number="np.arDirection" class="form-input-sm mt-1">
                  <option :value="0">{{ t('arNorth') }}</option>
                  <option :value="45">{{ t('arNE') }}</option>
                  <option :value="90">{{ t('arEast') }}</option>
                  <option :value="135">{{ t('arSE') }}</option>
                  <option :value="180">{{ t('arSouth') }}</option>
                  <option :value="225">{{ t('arSW') }}</option>
                  <option :value="270">{{ t('arWest') }}</option>
                  <option :value="315">{{ t('arNW') }}</option>
                </select>
                <div v-else class="ar-deg-input mt-1">
                  <input v-model.number="np.arDirection" type="number" min="0" max="359" class="form-input-sm" placeholder="0–359" />
                  <span class="ar-deg-unit">°</span>
                </div>
                <label class="field-label mt-2">{{ t('arEffectUrl') }}</label>
                <input v-model="np.arVideoUrl" class="form-input-sm" placeholder="https://..." />
                <label class="field-label mt-2">{{ t('arSearchWord') }}</label>
                <input v-model="np.arSearchWord" class="form-input-sm" :placeholder="t('arSearchWordPlaceholder')" />
                <div class="field-hint">{{ t('arSearchWordHint') }}</div>
                <div class="ar-gif-options">
                  <div class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arGifCount') }}</label>
                    <select v-model.number="np.arGifCount" class="form-input-sm">
                      <option :value="1">{{ t('arGif1') }}</option>
                      <option :value="2">{{ t('arGif2') }}</option>
                      <option :value="3">3</option>
                    </select>
                  </div>
                  <div v-if="np.arGifCount > 1" class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arSpacing') }}</label>
                    <input v-model.number="np.arGifSpacing" type="number" class="form-input-sm" placeholder="0" />
                    <div class="field-hint">{{ t('arSpacingHint') }}</div>
                  </div>
                </div>
                <label class="field-label mt-2">{{ t('arGrowAnimation') }}</label>
                <div class="ar-anim-toggle">
                  <button :class="['ar-dir-btn', np.arGrowAnimation !== false ? 'active' : '']" @click="np.arGrowAnimation = true">{{ t('arGrow') }}</button>
                  <button :class="['ar-dir-btn', np.arGrowAnimation === false ? 'active' : '']" @click="np.arGrowAnimation = false">{{ t('arAppear') }}</button>
                </div>
              </div>
              <div class="field-group">
                <label class="field-label">{{ t('image') }}</label>
                <div class="upload-hint">{{ t('imageUploadHint') }}</div>
                <input type="file" accept="image/*" class="form-input-sm mt-1" ref="npFileInput"
                  @change="onNewPointImagePicked" />
                <div v-if="np.imagePreview" class="thumb-row">
                  <img :src="np.imagePreview" class="point-thumb mt-2" alt="" />
                  <button class="btn-edit-photo" @click="openPhotoEditor(np)">{{ t('editPhoto') }}</button>
                </div>
                <div v-if="np.lat || np.lng" class="gps-badge">
                  📍 GPS: {{ np.lat }}, {{ np.lng }}
                </div>
              </div>
              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="newPoint = false; resetNewPoint()">{{ t('cancel') }}</button>
                <button class="btn-success" :disabled="loading" @click="submitNewPoint">
                  <span v-if="loading">{{ t('saving') }}</span>
                  <span v-else>{{ t('savePoint') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB QUESTIONS supprimé — questions intégrées dans chaque point -->
      <div v-show="false">
        <div v-if="loading" class="spinner-wrap">
          <div class="spinner"></div>
        </div>
        <div v-else>

          <div v-for="(q, index) in allQuestions" :key="q.key" class="card">
            <div class="card-header" @click="q._open = !q._open; $forceUpdate()">
              <div class="card-title">
                <span class="q-badge">Q{{ index + 1 }}</span>
                <span class="q-link" v-if="q.data.pointLinked">Point {{ q.data.pointLinked }}</span>
                <span class="q-text">{{ q.data.question }}</span>
              </div>
              <div class="card-actions">
                <button v-if="!q.editMode" class="btn-edit" @click.stop="q.editMode = true; $forceUpdate()">✏️</button>
                <button v-if="q.editMode" class="btn-save" @click.stop="saveQuestion(q)">💾</button>
                <button v-if="q.editMode" class="btn-cancel" @click.stop="q.editMode = false; $forceUpdate()">✕</button>
                <span v-if="!q.editMode" class="chevron">{{ q._open ? '▲' : '▼' }}</span>
              </div>
            </div>

            <!-- Read -->
            <div v-if="q._open && !q.editMode" class="card-body">
              <div class="answer-list">
                <div class="answer correct">✅ {{ q.data.correctAnswer }}</div>
                <div class="answer wrong">✗ {{ q.data.wrongAnswer1 }}</div>
                <div class="answer wrong">✗ {{ q.data.wrongAnswer2 }}</div>
                <div class="answer wrong">✗ {{ q.data.wrongAnswer3 }}</div>
              </div>
            </div>

            <!-- Edit -->
            <div v-if="q._open && q.editMode" class="card-body edit-form">
              <div class="field-group">
                <label class="field-label">Question</label>
                <textarea v-model="q.data.question" class="form-textarea" rows="2"></textarea>
              </div>
              <div class="field-group">
                <label class="field-label">✅ Correct answer</label>
                <input v-model="q.data.correctAnswer" class="form-input-sm correct-input" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 1</label>
                <input v-model="q.data.wrongAnswer1" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 2</label>
                <input v-model="q.data.wrongAnswer2" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 3</label>
                <input v-model="q.data.wrongAnswer3" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">Linked to point #</label>
                <input v-model="q.data.pointLinked" class="form-input-sm" placeholder="e.g. 1" />
              </div>
            </div>
          </div>

          <!-- Add question -->
          <div class="card add-card" @click="newQuestion = !newQuestion">
            ➕ Add a new question
          </div>

          <div v-if="newQuestion" class="card new-form-card">
            <div class="card-header">
              <span class="card-title">New question</span>
              <button class="btn-cancel" @click="newQuestion = false; resetNewQuestion()">✕</button>
            </div>
            <div class="card-body edit-form">
              <div class="field-group">
                <label class="field-label">Question text *</label>
                <textarea v-model="nq.question" class="form-textarea" rows="2"></textarea>
              </div>
              <div class="field-group">
                <label class="field-label">✅ Correct answer *</label>
                <input v-model="nq.correctAnswer" class="form-input-sm correct-input" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 1</label>
                <input v-model="nq.wrongAnswer1" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 2</label>
                <input v-model="nq.wrongAnswer2" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">✗ Wrong answer 3</label>
                <input v-model="nq.wrongAnswer3" class="form-input-sm" />
              </div>
              <div class="field-group">
                <label class="field-label">Linked to point #</label>
                <input v-model="nq.pointLinked" class="form-input-sm" placeholder="e.g. 1" />
              </div>
              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="newQuestion = false; resetNewQuestion()">Cancel</button>
                <button class="btn-success" @click="submitNewQuestion">💾 Save question</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─────────────────── TAB: SETTINGS ─────────────────── -->
      <div v-show="activeTab === 'settings'" class="tab-content">

        <!-- Map parameters -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsMap') }}</div>
          <div class="field-row">
            <div class="field-group">
              <label class="field-label">Center latitude</label>
              <input v-model="settings.mapLat" :class="['form-input-sm', { 'input-error': !settings.mapLat }]" placeholder="32.657" />
            </div>
            <div class="field-group">
              <label class="field-label">Center longitude</label>
              <input v-model="settings.mapLng" :class="['form-input-sm', { 'input-error': !settings.mapLng }]" placeholder="35.417" />
            </div>
            <div class="field-group">
              <label class="field-label">Zoom</label>
              <input v-model="settings.mapZoom" type="number" class="form-input-sm" placeholder="15" />
            </div>
            <button class="btn-map-pick" @click="openMapPicker(settings, 'mapLat', 'mapLng')">📍</button>
          </div>
          <button class="btn-success mt-3" @click="saveMapParameters">{{ t('saveMap') }}</button>
        </div>

        <!-- Display name -->
        <div class="settings-section">
          <div class="settings-title">{{ t('displayName') }}</div>
          <p class="muted" style="font-size:12px;margin-bottom:8px">Can include spaces and accents. This name appears on
            the player home page.</p>
          <input v-model="settings.displayName" class="form-input-sm" :placeholder="navigationName" />
          <button class="btn-success mt-3" @click="saveDisplayName">{{ t('saveDisplayName') }}</button>
        </div>

        <!-- Starting location -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsStartLocation') }}</div>
          <p class="muted" style="font-size:12px;margin-bottom:8px">If set, the player home page will show <em>"Make your way to the starting location: [name]"</em> instead of the default Waze phrase.</p>
          <input v-model="settings.startLocationName" class="form-input-sm" placeholder="e.g. Main Square, Town Hall Parking..." />
          <button class="btn-success mt-3" @click="saveStartLocationName">{{ t('saveStartLocation') }}</button>
        </div>

        <!-- Last point -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsLastPoint') }}</div>
          <input v-model.number="settings.lastPoint" type="number" :class="['form-input-sm', { 'input-error': !settings.lastPoint }]" placeholder="e.g. 5" min="1" />
          <button class="btn-success mt-3" @click="saveLastPoint">{{ t('saveLastPoint') }}</button>
        </div>

        <!-- Points order -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsOrders') }}</div>
          <p class="help-text">{{ t('ordersHint') }}</p>
          <div v-if="settings.pointsOrders.length === 0" class="muted mb-2">{{ t('noOrders') }}</div>
          <div v-for="(order, i) in settings.pointsOrders" :key="order.key || i" class="order-row">
            <span class="order-index">Ordre {{ i + 1 }}</span>
            <input v-model="order.str" class="form-input-sm" placeholder="1, 2, 3, 4, 5" />
            <button class="btn-danger-sm" @click="deleteOrder(order, i)">🗑️</button>
          </div>
          <div class="row-buttons mt-2">
            <button class="btn-ghost" @click="settings.pointsOrders.push({ key: null, str: '' })">{{ t('addOrder') }}</button>
            <button class="btn-success" @click="saveAllPointsOrders">{{ t('saveOrders') }}</button>
          </div>
        </div>

        <!-- Game URL -->
        <div class="settings-section">
          <div class="settings-title">{{ t('gameUrl') }}</div>
          <div class="url-display">
            <span class="url-text">{{ gameUrl }}</span>
            <button class="btn-copy-url" @click="copyGameUrl">{{ t('copy') }}</button>
          </div>
        </div>

        <!-- Game options -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsOptions') }}</div>
          <div class="checkbox-grid">
            <label class="check-label"><input type="checkbox" v-model="settings.randomQuestions" /> Questions
              aléatoires</label>
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Game mode</label>
            <div class="mode-cards">
              <div
                :class="['mode-card', { active: settings.questionsOrder === 'linkedToPointNumber' }]"
                @click="settings.questionsOrder = 'linkedToPointNumber'">
                <div class="mode-card-icon">📍</div>
                <div class="mode-card-title">Geographic</div>
                <div class="mode-card-desc">Each point's content is tied to its physical location. Each group sees the content of the point where they are standing. <em>Ideal for monuments, historical sites, landmarks.</em></div>
              </div>
              <div
                :class="['mode-card', { active: settings.questionsOrder === 'sorted' }]"
                @click="settings.questionsOrder = 'sorted'">
                <div class="mode-card-icon">📖</div>
                <div class="mode-card-title">Story</div>
                <div class="mode-card-desc">The story is told in sequential order regardless of physical location. All groups receive the same chapters in the same order, even if they visit different places. <em>Ideal for narratives, guided adventures.</em></div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Toast notification -->
    <div v-if="toast.show" :class="['toast-msg', toast.type]">{{ toast.text }}</div>

    <!-- ─── PHOTO EDITOR ─── -->
    <PhotoEditor
      v-if="photoEditor.open"
      :src="photoEditor.src"
      @save="onPhotoEditorSave"
      @cancel="photoEditor.open = false"
    />

    <!-- ─── IMAGE CROPPER ─── -->
    <ImageCropper
      v-if="imageCropper.show"
      :src="imageCropper.src"
      @save="onCropperSave"
      @cancel="onCropperCancel"
    />

    <!-- ─── MAP PICKER OVERLAY ─── -->
    <div v-if="mapPicker.active" class="map-picker-overlay">
      <div class="map-picker-header">
        <span>{{ t('mapPickerHint') }}</span>
        <span v-if="mapPicker.pickedLat" class="map-picker-coords">{{ mapPicker.pickedLat }}, {{ mapPicker.pickedLng
        }}</span>
        <div class="map-picker-actions">
          <button class="btn-success" @click="confirmMapPicker">{{ t('confirm') }}</button>
          <button class="btn-danger" @click="closeMapPicker">{{ t('cancel') }}</button>
        </div>
      </div>
      <div id="admin-map-picker-div" class="map-picker-map"></div>
    </div>

  </div>
</template>

<script>
import PhotoEditor from './PhotoEditor.vue';
import ImageCropper from './ImageCropper.vue';
import RushHourEditor from './rushHourEditor.vue';
import { adminUI } from '../i18n/adminUI.js';

export default {
  components: { PhotoEditor, ImageCropper, RushHourEditor },
  data() {
    return {
      // Auth
      authenticated: false,
      authError: false,
      authLoading: false,

      // Game selection
      navigationName: '',
      currentLang: 'he',
      availableLangs: [],
      allNavGameNames: [],

      // New game config
      cfg: {
        randomQuestions: false,
        questionsOrder: 'linkedToPointNumber',
        languages: ['he'],
        mapLat: '',
        mapLng: '',
        lastPoint: '',
        visitOrder: '1, 2, 3',
        startLocationName: '',
      },
      activeInfo: null,

      // Editor state
      activeTab: 'points',
      loading: false,

      // Points
      allPoints: [],
      newPoint: false,
      np: this.freshPoint(),

      // Questions
      allQuestions: [],
      newQuestion: false,
      nq: this.freshQuestion(),

      // Settings
      settings: {
        displayName: '',
        startLocationName: '',
        mapLat: '',
        mapLng: '',
        mapZoom: 15,
        lastPoint: '',
        pointsOrders: [],
        randomQuestions: false,
        questionsOrder: 'linkedToPointNumber',
      },

      adminLang: localStorage.getItem('adminLang') || 'fr',

      // Toast
      toast: { show: false, text: '', type: 'success' },
      photoEditor: { open: false, src: '', target: null },
      imageCropper: { show: false, src: '', _resolve: null },

      // Map picker
      mapPicker: { active: false, obj: null, latKey: 'lat', lngKey: 'lng', pickedLat: null, pickedLng: null, _map: null, _marker: null },
    };
  },

  mounted() {
    const firebase = require('firebase/app').default;
    firebase.auth().onAuthStateChanged(async (user) => {
      if (!user) { this.authenticated = false; this.authLoading = false; return; }
      const snap = await firebase.database().ref('admins/' + user.uid).once('value');
      if (snap.val() === true) {
        this.authenticated = true;
        this.authError = false;
        this.loadAllGames();
      } else {
        await firebase.auth().signOut();
        this.authenticated = false;
        this.authError = true;
      }
      this.authLoading = false;
    });
  },

  computed: {
    gameUrl() {
      return window.location.origin + '/' + this.navigationName;
    },
  },

  watch: {
    adminLang(val) { localStorage.setItem('adminLang', val); },
  },

  methods: {

    t(key) {
      const lang = this.adminLang;
      return (adminUI[lang] && adminUI[lang][key]) || (adminUI['fr'] && adminUI['fr'][key]) || key;
    },

    async openMapPicker(obj, latKey, lngKey) {
      this.mapPicker.obj = obj;
      this.mapPicker.latKey = latKey;
      this.mapPicker.lngKey = lngKey;
      this.mapPicker.pickedLat = parseFloat(obj[latKey]) || null;
      this.mapPicker.pickedLng = parseFloat(obj[lngKey]) || null;
      this.mapPicker.active = true;
      // Wait for Google Maps API to be ready (loaded lazily by @fawmi/vue-google-maps)
      await this.$gmapApiPromiseLazy();
      await this.$nextTick();
      const defaultLat = this.mapPicker.pickedLat || parseFloat(this.settings.mapLat) || parseFloat(this.cfg.mapLat) || 32.657;
      const defaultLng = this.mapPicker.pickedLng || parseFloat(this.settings.mapLng) || parseFloat(this.cfg.mapLng) || 35.417;
      const center = { lat: defaultLat, lng: defaultLng };
      const map = new window.google.maps.Map(document.getElementById('admin-map-picker-div'), {
        center, zoom: 15, disableDefaultUI: false,
      });
      let marker = null;
      if (this.mapPicker.pickedLat) {
        marker = new window.google.maps.Marker({ position: center, map });
      }
      map.addListener('click', (e) => {
        const lat = parseFloat(e.latLng.lat().toFixed(7));
        const lng = parseFloat(e.latLng.lng().toFixed(7));
        this.mapPicker.pickedLat = lat;
        this.mapPicker.pickedLng = lng;
        if (marker) marker.setMap(null);
        marker = new window.google.maps.Marker({ position: { lat, lng }, map });
      });
      this.mapPicker._map = map;
      this.mapPicker._marker = marker;
    },

    confirmMapPicker() {
      if (this.mapPicker.pickedLat !== null) {
        this.mapPicker.obj[this.mapPicker.latKey] = String(this.mapPicker.pickedLat);
        this.mapPicker.obj[this.mapPicker.lngKey] = String(this.mapPicker.pickedLng);
      }
      this.closeMapPicker();
    },

    closeMapPicker() {
      this.mapPicker.active = false;
      this.mapPicker._map = null;
      this.mapPicker._marker = null;
    },

    // ─── AUTH ─────────────────────────────────────────────────
    async signInWithGoogle() {
      this.authLoading = true;
      this.authError = false;
      const firebase = require('firebase/app').default;
      const provider = new firebase.auth.GoogleAuthProvider();
      try {
        await firebase.auth().signInWithPopup(provider);
        // onAuthStateChanged dans mounted() prend le relais
      } catch (e) {
        this.authError = true;
        this.authLoading = false;
      }
    },

    async logout() {
      const firebase = require('firebase/app').default;
      await firebase.auth().signOut();
      this.authenticated = false;
      this.navigationName = '';
    },

    // ─── GAME SELECTION ────────────────────────────────────────
    async loadAllGames() {
      this.loading = true;
      await this.$store.dispatch('fetchAllNavGames');
      this.allNavGameNames = this.$store.getters.allNavGameNames || [];
      this.loading = false;
    },

    toggleInfo(field) {
      this.activeInfo = this.activeInfo === field ? null : field;
    },

    async startEditing(name) {
      this.navigationName = name;
      this.activeTab = 'points';
      // Load available languages for this game
      const firebase = require('firebase/app').default;
      const snap = await firebase.database().ref(name + '/languages').once('value');
      this.availableLangs = snap.val() || ['he'];
      this.currentLang = this.availableLangs[0] || 'he';
      await Promise.all([this.loadPoints(), this.loadQuestions()]);
    },

    async switchLang(lang) {
      this.currentLang = lang;
      await Promise.all([this.loadPoints(), this.loadQuestions()]);
    },

    // ─── POINTS ────────────────────────────────────────────────
    async loadPoints() {
      this.loading = true;
      await this.$store.dispatch('fetchPoints', { name: this.navigationName, lang: this.currentLang });
      const pts = this.$store.getters.points || [];
      const markers = this.$store.getters.markers || [];
      const defaults = this.freshPoint();
      this.allPoints = pts.map(p => {
        const marker = markers[p.pointNumber] || {};
        const merged = {
          ...defaults,  // fill any missing fields with defaults
          ...p,         // actual DB data wins over defaults
          lat: marker.lat !== undefined ? marker.lat : (p.lat || ''),
          lng: marker.lng !== undefined ? marker.lng : (p.lng || ''),
        };
        return { ...merged, _open: false, editMode: false, _original: JSON.stringify(merged), _addingQuestion: false, _nq: this.freshQuestion() };
      }).sort((a, b) => Number(a.pointNumber) - Number(b.pointNumber));
      this.loading = false;
    },

    togglePoint(point) {
      if (!point.editMode) point._open = !point._open;
    },

    openNewPointForm() {
      this.np = this.freshPoint();
      this.newPoint = true;
    },

    cancelEdit(point) {
      Object.assign(point, JSON.parse(point._original));
      point.editMode = false;
      point._open = false;
    },

    addWaypoint(point) {
      if (!point.waypoints) point.waypoints = [];
      point.waypoints.push({ lat: '', lng: '' });
    },

    normalizeJFItems(items) {
      if (!items) return [];
      return Array.isArray(items) ? items : Object.values(items);
    },

    removeJusteFauxItem(target, idx) {
      const arr = this.normalizeJFItems(target.justeFauxItems);
      arr.splice(idx, 1);
      target.justeFauxItems = [...arr];
      this.$forceUpdate();
    },

    addJusteFauxItem(target) {
      // Firebase may return arrays as numeric-keyed objects — normalize before push
      const existing = target.justeFauxItems;
      let arr;
      if (!existing) {
        arr = [];
      } else if (Array.isArray(existing)) {
        arr = [...existing];
      } else {
        arr = Object.values(existing);
      }
      arr.push({ label: '', correct: true });
      target.justeFauxItems = arr;
      this.$forceUpdate();
    },

    async savePoint(point) {
      const hasInfo = !!(point.audioUrl || point.pointInformation || point.videoUrl || point.withAR || point.withPhone);
      if (!hasInfo) {
        this.showErrorToast('At least one info field is required: audio URL, text, video, AR, or Phone.');
        return;
      }
      this.loading = true;
      const oldNumber = JSON.parse(point._original).pointNumber;
      const newNumber = point.pointNumber;

      if (String(oldNumber) !== String(newNumber)) {
        await this.$store.dispatch('renumberPoint', {
          navigationName: this.navigationName,
          oldNumber,
          newNumber,
        });
      }

      const result = await this.$store.dispatch('updatePoint', {
        ...point,
        navigationName: this.navigationName,
        lang: this.currentLang,
      });
      // Keep the marker in sync with the edited lat/lng
      await this.$store.dispatch('createNewMarker', {
        navigationName: this.navigationName,
        pointNumber: newNumber,
        position: { lat: point.lat, lng: point.lng },
      });
      // Refresh localStorage cache so the game picks up the saved changes immediately
      await this.$store.dispatch('fetchPoints', { name: this.navigationName, lang: this.currentLang });
      if (result && result.imageUrl) point.imageUrl = result.imageUrl;
      point._file = null;
      point.imagePreview = '';
      point.editMode = false;
      point._original = JSON.stringify(point);
      this.loading = false;
      this.showSuccessToast(this.t('toastPointSaved'));
    },

    async submitNewPoint() {
      if (!this.np.pointNumber || !this.np.lat || !this.np.lng) {
        this.showErrorToast('Point number, lat and lng are required.');
        return;
      }
      if (!this.np.audioUrl && !this.np.pointInformation && !this.np.videoUrl) {
        this.showErrorToast('At least one info field is required: audio URL, point information, or video ID.');
        return;
      }
      this.loading = true;
      await this.$store.dispatch('createNewPoint', {
        ...this.np,
        navigationName: this.navigationName,
        lang: this.currentLang,
        image: this.np._file || null,
      });
      // Save the GPS marker for the new point
      await this.$store.dispatch('createNewMarker', {
        navigationName: this.navigationName,
        pointNumber: this.np.pointNumber,
        position: { lat: this.np.lat, lng: this.np.lng },
      });
      this.newPoint = false;
      this.resetNewPoint();
      await this.loadPoints();
      this.showSuccessToast(this.t('toastPointCreated'));
    },

    resetNewPoint() {
      this.np = this.freshPoint();
    },

    openPhotoEditor(point, mmKey) {
      const src = mmKey ? point[mmKey] : (point.imagePreview || point.imageUrl);
      if (!src) return;
      this.photoEditor = { open: true, src, target: point, mmKey: mmKey || null };
    },

    onPhotoEditorSave(dataUrl) {
      const { target, mmKey } = this.photoEditor;
      if (mmKey) {
        target[mmKey] = dataUrl;
      } else {
        target.imagePreview = dataUrl;
        target.imageBase64  = dataUrl;
        fetch(dataUrl).then(r => r.blob()).then(blob => { target._file = blob; });
      }
      this.photoEditor.open = false;
    },

    processMemoryImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const SIZE = 120;
            const canvas = document.createElement('canvas');
            canvas.width = SIZE;
            canvas.height = SIZE;
            const ctx = canvas.getContext('2d');
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    },

    async onMemoryImagePicked(event, target) {
      const file = event.target.files[0];
      if (!file) return;
      if (!target.memoryImages) this.$set(target, 'memoryImages', []);
      if (target.memoryImages.length >= 6) return;
      const dataUrl = await this.processMemoryImage(file);
      target.memoryImages.push(dataUrl);
      this.$forceUpdate();
      event.target.value = '';
    },

    parseVideoInput(val, point) {
      const shortsMatch = val.match(/shorts\/([a-zA-Z0-9_-]{11})/);
      const normalMatch = val.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (shortsMatch) {
        point.videoUrl = shortsMatch[1];
        point.withVerticalVideo = true;
      } else if (normalMatch) {
        point.videoUrl = normalMatch[1];
        point.withVerticalVideo = false;
      } else {
        point.videoUrl = val.trim();
        point.withVerticalVideo = false;
      }
      this.$forceUpdate();
    },

    freshPoint() {
      return {
        pointNumber: '',
        lat: '',
        lng: '',
        navigationScreen: 'imagesList',
        picked: 'questions',
        missingWord: '',
        pointInformation: '',
        audioUrl: '',
        withVideo: false, withVerticalVideo: false, withAR: false,
        videoUrl: '', videoBrightness: 100,
        image: '',
        imageBase64: '',
        imageUrl: '',
        noGoogleRoute: false,
        photoInstruction: '',
        pictures: {},
        timePerPoint: 2,
        imagePreview: '',
        puzzleImageUrl: '',
        puzzleImagePreview: '',
        puzzleImageBase64: '',
        itemImage1: '', itemImage2: '', itemImage3: '', itemImage4: '',
        description1: '', description2: '', description3: '', description4: '',
        waypoints: [],
        arDirection: 180, arDirectionExact: false, arVideoUrl: '', arGifCount: 1, arGifSpacing: 0, arSearchWord: '',
        memoryImages: [],
        signCardImageBase64: '', signCardImageUrl: '', signCardImagePreview: '',
        signCardSigHeightPct: 20, signCardSigVAlign: 'bottom', signCardSigHAlign: 'center',
        _file: null,
        _puzzleFile: null,
      };
    },

    /* ── Image cropper ── */
    openCropper(img) {
      const tmp = document.createElement('canvas');
      tmp.width = img.width; tmp.height = img.height;
      tmp.getContext('2d').drawImage(img, 0, 0);
      const dataUrl = tmp.toDataURL('image/jpeg', 0.95);
      return new Promise((resolve) => {
        this.imageCropper = { show: true, src: dataUrl, _resolve: resolve };
      });
    },

    onCropperSave(croppedDataUrl) {
      this.imageCropper.show = false;
      const croppedImg = new Image();
      croppedImg.onload = () => this.imageCropper._resolve(croppedImg);
      croppedImg.src = croppedDataUrl;
    },

    onCropperCancel() {
      this.imageCropper.show = false;
      this.imageCropper._resolve(null);
    },

    // Image picked for NEW point — extract EXIF GPS automatically
    onNewPointImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      const bufReader = new FileReader();
      bufReader.onload = (e) => {
        const gps = this.extractGpsFromBuffer(e.target.result);
        if (gps) { this.np.lat = gps.lat; this.np.lng = gps.lng; }
      };
      bufReader.readAsArrayBuffer(file);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const finalImg = await this.openCropper(img);
        if (!finalImg) return;
        const tmp = document.createElement('canvas');
        tmp.width = finalImg.width; tmp.height = finalImg.height;
        tmp.getContext('2d').drawImage(finalImg, 0, 0);
        const dataUrl = tmp.toDataURL('image/jpeg', 0.92);
        this.np.imagePreview = dataUrl;
        this.np.imageBase64 = dataUrl;
        tmp.toBlob((blob) => { this.np._file = blob; }, 'image/jpeg', 0.92);
      };
      img.src = url;
    },

    // Image picked for EXISTING point (edit mode)
    onImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const gps = this.extractGpsFromBuffer(e.target.result);
        if (gps) { point.lat = gps.lat; point.lng = gps.lng; }
      };
      reader.readAsArrayBuffer(file);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const finalImg = await this.openCropper(img);
        if (!finalImg) return;
        const tmp = document.createElement('canvas');
        tmp.width = finalImg.width; tmp.height = finalImg.height;
        tmp.getContext('2d').drawImage(finalImg, 0, 0);
        const dataUrl = tmp.toDataURL('image/jpeg', 0.92);
        point.imageUrl = dataUrl;
        point.imagePreview = dataUrl;
        point.imageBase64 = dataUrl;
        tmp.toBlob((blob) => { point._file = blob; }, 'image/jpeg', 0.92);
      };
      img.src = url;
    },


    arDirectionLabel(deg) {
      const dirs = { 0: 'North', 45: 'NE', 90: 'East', 135: 'SE', 180: 'South', 225: 'SW', 270: 'West', 315: 'NW' };
      return dirs[deg] !== undefined ? dirs[deg] + ' (' + deg + '°)' : (deg !== undefined ? deg + '°' : '—');
    },

    async onMmImagePicked(event, point, n) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processMmImage(file);
      point['itemImage' + n] = dataUrl;
    },

    // Multiple-match images — new point
    async onNewPointMmImagePicked(event, n) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processMmImage(file);
      this.np['itemImage' + n] = dataUrl;
    },

    // Center-crop to 150×200 portrait, compress to JPEG
    processMmImage(file) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const targetW = 150, targetH = 200;
          const ratio = targetW / targetH;
          let sx, sy, sw, sh;
          if (img.width / img.height > ratio) {
            sh = img.height;
            sw = img.height * ratio;
            sy = 0;
            sx = (img.width - sw) / 2;
          } else {
            sw = img.width;
            sh = img.width / ratio;
            sx = 0;
            sy = (img.height - sh) / 2;
          }
          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.src = url;
      });
    },

    // Puzzle image for NEW point
    async onNewPointPuzzleImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processPuzzleImage(file);
      this.np.puzzleImagePreview = dataUrl;
      this.np.puzzleImageBase64 = dataUrl;
    },

    // Puzzle image for EXISTING point (edit mode)
    async onPuzzleImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processPuzzleImage(file);
      point.puzzleImageUrl = dataUrl;
      point.puzzleImageBase64 = dataUrl;
    },

    // SignCard image upload handlers
    async onSignCardImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processSignCardImage(file);
      point.signCardImageUrl = dataUrl;
      point.signCardImageBase64 = dataUrl;
    },

    async onNewSignCardImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processSignCardImage(file);
      this.np.signCardImagePreview = dataUrl;
      this.np.signCardImageBase64 = dataUrl;
    },

    processSignCardImage(file) {
      return new Promise(resolve => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const maxW = 800;
          const scale = img.width > maxW ? maxW / img.width : 1;
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.src = url;
      });
    },

    signCardZoneStyle(data) {
      const pct = data.signCardSigHeightPct || 20;
      const vAlign = data.signCardSigVAlign || 'bottom';
      const hAlign = data.signCardSigHAlign || 'center';
      const transforms = [];
      const style = {
        position: 'absolute', height: pct + '%', width: '72%',
        border: '2px dashed rgba(255,255,255,0.75)', borderRadius: '4px',
        background: 'rgba(255,255,255,0.12)', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '700',
        pointerEvents: 'none',
      };
      if (vAlign === 'top')    style.top    = '4%';
      else if (vAlign === 'center') { style.top = '50%'; transforms.push('translateY(-50%)'); }
      else                    style.bottom = '4%';
      if (hAlign === 'left')   style.left   = '4%';
      else if (hAlign === 'center') { style.left = '50%'; transforms.push('translateX(-50%)'); }
      else                    style.right  = '4%';
      if (transforms.length) style.transform = transforms.join(' ');
      return style;
    },

    // Center-crop to square, resize to 600×600, compress to JPEG
    processPuzzleImage(file) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const size = Math.min(img.width, img.height);
          const sx = (img.width - size) / 2;
          const sy = (img.height - size) / 2;
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = 400;
          canvas.getContext('2d').drawImage(img, sx, sy, size, size, 0, 0, 400, 400);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.src = url;
      });
    },

    // Native EXIF GPS extraction — no external library needed
    extractGpsFromBuffer(buffer) {
      try {
        const view = new DataView(buffer);
        if (view.byteLength < 4 || view.getUint16(0) !== 0xFFD8) return null;

        // Scan for APP1 marker (0xFFE1) containing Exif header
        let offset = 2;
        let tiffStart = -1;
        while (offset + 4 < view.byteLength) {
          const marker = view.getUint16(offset);
          const segLen = view.getUint16(offset + 2);
          if (marker === 0xFFDA) break; // start of scan — no more app segments
          if (marker === 0xFFE1 &&
            view.getUint8(offset + 4) === 0x45 && // E
            view.getUint8(offset + 5) === 0x78 && // x
            view.getUint8(offset + 6) === 0x69 && // i
            view.getUint8(offset + 7) === 0x66 && // f
            view.getUint8(offset + 8) === 0x00 &&
            view.getUint8(offset + 9) === 0x00) {
            tiffStart = offset + 10; // skip marker(2) + length(2) + "Exif\0\0"(6)
            break;
          }
          offset += 2 + segLen;
        }
        if (tiffStart < 0) return null;

        // TIFF byte order
        const bo = view.getUint16(tiffStart);
        const le = bo === 0x4949; // 'II' = little-endian, 'MM' = big-endian
        const r16 = (o) => view.getUint16(tiffStart + o, le);
        const r32 = (o) => view.getUint32(tiffStart + o, le);
        const r8 = (o) => view.getUint8(tiffStart + o);
        if (r16(2) !== 42) return null; // TIFF magic number check

        // IFD0: find GPS sub-IFD pointer (tag 0x8825)
        const ifd0 = r32(4);
        const n0 = r16(ifd0);
        let gpsOff = null;
        for (let i = 0; i < n0; i++) {
          const e = ifd0 + 2 + i * 12;
          if (r16(e) === 0x8825) { gpsOff = r32(e + 8); break; }
        }
        if (gpsOff === null) return null;

        // Parse GPS IFD entries
        const nGps = r16(gpsOff);
        let latRef = 'N', lngRef = 'E', latRaw = null, lngRaw = null;

        for (let i = 0; i < nGps; i++) {
          const e = gpsOff + 2 + i * 12;
          const tag = r16(e);
          const valOff = r32(e + 8); // offset to value (for types > 4 bytes)

          if (tag === 0x0001) { latRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0003) { lngRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0002) { // GPSLatitude: 3 RATIONAL values (deg, min, sec)
            latRaw = [
              r32(valOff) / r32(valOff + 4),
              r32(valOff + 8) / r32(valOff + 12),
              r32(valOff + 16) / r32(valOff + 20),
            ];
          } else if (tag === 0x0004) { // GPSLongitude: 3 RATIONAL values
            lngRaw = [
              r32(valOff) / r32(valOff + 4),
              r32(valOff + 8) / r32(valOff + 12),
              r32(valOff + 16) / r32(valOff + 20),
            ];
          }
        }

        if (!latRaw || !lngRaw) return null;

        const toDD = (dms, ref) => {
          const dd = dms[0] + dms[1] / 60 + dms[2] / 3600;
          return (ref === 'S' || ref === 'W') ? -dd : dd;
        };
        return {
          lat: toDD(latRaw, latRef).toFixed(6),
          lng: toDD(lngRaw, lngRef).toFixed(6),
        };
      } catch {
        return null;
      }
    },

    // ─── QUESTIONS ─────────────────────────────────────────────
    async loadQuestions() {
      this.loading = true;
      await this.$store.dispatch('fetchQuestionsOnly', { name: this.navigationName, lang: this.currentLang });
      const qs = this.$store.getters.questionsList || [];
      this.allQuestions = qs.map(q => ({ ...q, _open: false, editMode: false }));
      this.loading = false;
    },

    async saveQuestion(q) {
      await this.$store.dispatch('updatQuestion', {
        navigationName: this.navigationName,
        lang: this.currentLang,
        key: q.key,
        question: q.data,
      });
      q.editMode = false;
      this.showSuccessToast(this.t('toastQuestionSaved'));
    },

    questionsForPoint(pointNumber) {
      return this.allQuestions.filter(q => String(q.data.pointLinked) === String(pointNumber));
    },

    async submitQuestionForPoint(point) {
      const nq = point._nq;
      if (!nq.question || !nq.correctAnswer) {
        this.showErrorToast('Question et bonne réponse obligatoires.');
        return;
      }
      await this.$store.dispatch('createNewQuestion', {
        navigationName: this.navigationName,
        lang: this.currentLang,
        question: { ...nq, pointLinked: point.pointNumber },
      });
      point._addingQuestion = false;
      point._nq = this.freshQuestion();
      await this.loadQuestions();
      this.showSuccessToast('Question créée !');
    },

    async submitNewQuestion() {
      if (!this.nq.question || !this.nq.correctAnswer) {
        this.showErrorToast('Question text and correct answer are required.');
        return;
      }
      await this.$store.dispatch('createNewQuestion', {
        navigationName: this.navigationName,
        lang: this.currentLang,
        question: { ...this.nq },
      });
      this.newQuestion = false;
      this.resetNewQuestion();
      await this.loadQuestions();
      this.showSuccessToast(this.t('toastQuestionCreated'));
    },

    resetNewQuestion() {
      this.nq = this.freshQuestion();
    },

    freshQuestion() {
      return {
        question: '',
        correctAnswer: '',
        wrongAnswer1: '',
        wrongAnswer2: '',
        wrongAnswer3: '',
        pointLinked: '',
      };
    },

    // ─── SETTINGS ──────────────────────────────────────────────
    async loadSettings() {
      this.loading = true;
      const firebase = require('firebase/app').default;
      const snap = await firebase.database().ref(this.navigationName).once('value');
      this.loading = false;
      const d = snap.val() || {};
      const mp = d.mapParameters || {};
      this.settings.mapLat = mp.lat || '';
      this.settings.mapLng = mp.lng || '';
      this.settings.mapZoom = mp.zoom || 15;
      this.settings.displayName = d.displayName || '';
      this.settings.startLocationName = d.startLocationName || '';
      this.settings.lastPoint = d.lastPoint || '';
      this.settings.randomQuestions = !!d.randomQuestions;
      this.settings.questionsOrder = d.questionsOrder || 'linkedToPointNumber';
      const orders = d.pointsOrder;
      if (orders && typeof orders === 'object') {
        this.settings.pointsOrders = Object.entries(orders).map(([key, arr]) => ({
          key,
          str: (Array.isArray(arr) ? arr : Object.values(arr)).join(', '),
        }));
      } else {
        this.settings.pointsOrders = [];
      }
    },

    saveDisplayName() {
      this.$store.dispatch('saveDisplayName', {
        navigationName: this.navigationName,
        displayName: this.settings.displayName.trim(),
      });
      this.showSuccessToast(this.t('toastDisplayNameSaved'));
    },

    saveStartLocationName() {
      this.$store.dispatch('saveStartLocationName', {
        navigationName: this.navigationName,
        startLocationName: this.settings.startLocationName.trim(),
      });
      this.showSuccessToast(this.t('toastStartLocationSaved'));
    },

    saveMapParameters() {
      this.$store.dispatch('storeMapParameters', {
        navigationName: this.navigationName,
        lat: this.settings.mapLat,
        lng: this.settings.mapLng,
        zoom: this.settings.mapZoom,
      });
      this.showSuccessToast(this.t('toastMapSaved'));
    },

    saveLastPoint() {
      this.$store.dispatch('storeEndPoint', {
        navigationName: this.navigationName,
        endPoint: this.settings.lastPoint,
      });
      this.showSuccessToast(this.t('toastLastPointSaved'));
    },

    savePointsOrders() { this.saveAllPointsOrders(); },

    async saveAllPointsOrders() {
      for (const order of this.settings.pointsOrders) {
        const arr = order.str.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
        if (!arr.length) continue;
        if (order.key) {
          await this.$store.dispatch('updatePointsOrder', {
            navigationName: this.navigationName,
            key: order.key,
            order: arr,
          });
        } else {
          await this.$store.dispatch('storePointsOrder', {
            navigationName: this.navigationName,
            pointsOrder: arr,
          });
        }
      }
      this.showSuccessToast(this.t('toastOrdersSaved'));
    },

    async deleteOrder(order, index) {
      if (order.key) {
        await this.$store.dispatch('deletePointsOrder', {
          navigationName: this.navigationName,
          key: order.key,
        });
      }
      this.settings.pointsOrders.splice(index, 1);
      this.showSuccessToast('Ordre supprimé !');
    },

    copyGameUrl() {
      navigator.clipboard.writeText(this.gameUrl).then(() => { this.showSuccessToast(this.t('toastUrlCopied')); });
    },

    // ─── TOAST ────────────────────────────────────────────────
    showSuccessToast(text) {
      this.toast = { show: true, text, type: 'success' };
      setTimeout(() => { this.toast.show = false; }, 2500);
    },
    showErrorToast(text) {
      this.toast = { show: true, text, type: 'error' };
      setTimeout(() => { this.toast.show = false; }, 3000);
    },
  },
};
</script>

<style scoped>
/* ── Global ─────────────────────────────────────── */
* {
  box-sizing: border-box;
}

/* ── Auth & selection screen ─────────────────────── */
.auth-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
}

.auth-card {
  background: white;
  border-radius: 16px;
  padding: 40px 32px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.auth-logo {
  font-size: 48px;
  margin-bottom: 12px;
}

.auth-card h2 {
  color: #1a1a2e;
  margin: 0 0 8px;
  font-size: 24px;
}

.auth-card .text-muted {
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
}

.error-msg {
  color: #e53935;
  font-size: 13px;
  margin: 8px 0;
}

.choice-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}

.btn-choice {
  padding: 16px;
  border-radius: 10px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  color: white;
  transition: opacity 0.2s;
}

.btn-choice:hover {
  opacity: 0.88;
}

.btn-choice.green {
  background: #2e7d32;
}

.btn-choice.blue {
  background: #1565c0;
}

.game-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.game-item {
  padding: 12px 16px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: #1b5e20;
  text-align: left;
  transition: background 0.15s;
}

.game-item:hover {
  background: #c8e6c9;
}

/* ── Top bar ─────────────────────────────────────── */
.topbar {
  background: #1a73e8;
  color: white;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: sticky;
  top: 0;
  z-index: 50;
}

.topbar-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.topbar-row--title {
  justify-content: center;
}

.topbar-row--tabs {
  gap: 8px;
}

.topbar-row--actions {
  gap: 8px;
  flex-wrap: wrap;
}

.topbar-game {
  font-weight: 700;
  font-size: 16px;
  white-space: nowrap;
}

.tab-btn {
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: background 0.15s;
}

.tab-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.tab-btn.active {
  background: white;
  color: #1a73e8;
  font-weight: 700;
}

.topbar-back {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: white;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  margin-inline-start: auto;
}
.topbar-back:hover { background: rgba(255, 255, 255, 0.15); }
.topbar-logout {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: white;
  font-size: 12px;
  cursor: pointer;
}

/* ── Tab content ─────────────────────────────────── */
.tab-content {
  padding: 16px;
  max-width: 720px;
  margin: 0 auto;
}

/* ── Cards ───────────────────────────────────────── */
.card {
  background: white;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid transparent;
  transition: background 0.15s;
}

.card-header:hover {
  background: #f5f5f5;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.point-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.point-meta-row {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.card-body {
  padding: 16px;
}

.add-card {
  border: 2px dashed #b0bec5;
  background: #fafafa;
  text-align: center;
  padding: 20px;
  color: #546e7a;
  font-weight: 600;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.15s;
}

.add-card:hover {
  border-color: #1a73e8;
  color: #1a73e8;
  background: #e8f0fe;
}

.new-form-card {
  border: 2px solid #1a73e8;
}

.new-form-card .card-header {
  background: #e8f0fe;
}

.chevron {
  font-size: 12px;
  color: #9e9e9e;
}

/* ── Badges ──────────────────────────────────────── */
.point-badge {
  background: #1a73e8;
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.point-label {
  font-size: 10px;
  color: #9e9e9e;
  font-weight: 500;
  white-space: nowrap;
}

.point-screen-badge,
.point-picked-badge {
  background: #e8f0fe;
  color: #1565c0;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
}

.point-picked-badge {
  background: #fce4ec;
  color: #880e4f;
}

.point-info-badge {
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
}

.point-info-badge.text {
  background: #e8f5e9;
  color: #2e7d32;
}

.point-info-badge.video {
  background: #ede7f6;
  color: #512da8;
}

.point-info-badge.audio {
  background: #fff3e0;
  color: #e65100;
}
.point-info-badge.ar { background: #e8eaf6; color: #3949ab; }
.point-info-badge.phone { background: #e8f5e9; color: #2e7d32; }

.point-info-badge.empty {
  color: #bdbdbd;
}

.point-missing-word {
  font-size: 11px;
  font-weight: 600;
  color: #333;
  background: #fff9c4;
  border-radius: 4px;
  padding: 2px 7px;
}

.point-q-badge {
  background: #f5f5f5;
  color: #616161;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 4px;
}

/* ── Section questions dans chaque point ── */
.point-questions-section {
  border-top: 1px solid #e8eaf6;
  background: #f8f9ff;
  padding: 12px 16px;
}

.pqs-header {
  font-size: 12px;
  font-weight: 700;
  color: #3949ab;
  margin-bottom: 8px;
}

.pq-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 6px;
  overflow: hidden;
}

.pq-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  gap: 8px;
}

.pq-card-header:hover {
  background: #f5f5f5;
}

.pq-text {
  font-size: 13px;
  color: #333;
  flex: 1;
}

.pq-body {
  padding: 8px 12px;
}

.pq-new {
  padding: 12px;
  border: 1px dashed #3949ab;
  background: #f3f4ff;
}

.pqs-add-btn {
  width: 100%;
  padding: 7px;
  border: 1px dashed #3949ab;
  border-radius: 6px;
  background: transparent;
  color: #3949ab;
  font-size: 12px;
  cursor: pointer;
  margin-top: 4px;
}

.pqs-add-btn:hover {
  background: #e8eaf6;
}

.q-badge {
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.q-link {
  background: #fff3e0;
  color: #e65100;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.q-text {
  color: #333;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gps-badge {
  margin-top: 8px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
}

/* ── Info grid ───────────────────────────────────── */
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item .label {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
}

.info-text {
  font-size: 13px;
  color: #555;
  white-space: pre-wrap;
  background: #f9f9f9;
  padding: 10px;
  border-radius: 6px;
  margin: 4px 0 0;
}

/* ── Read view ───────────────────────────────────── */
.read-section {
  margin-bottom: 14px;
}

.read-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.read-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.read-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.read-item .label {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
}

.read-item span:last-child {
  font-size: 13px;
  color: #333;
}

.read-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 6px;
}

.read-field>.label {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
}

.read-value {
  font-size: 13px;
  color: #333;
}

.muted {
  color: #bdbdbd;
  font-size: 13px;
}

.read-value {
  font-size: 13px;
  color: #333;
  word-break: break-all;
}

.file-btn {
  display: inline-block;
  padding: 7px 14px;
  background: #e8f0fe;
  color: #1a73e8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid #c5d8fc;
  transition: background 0.15s;
}
.file-btn:hover { background: #d2e3fc; }
.point-thumb {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.ar-compile-progress { margin-top: 8px; font-size: 13px; color: #e65100; background: #fff3e0; padding: 6px 10px; border-radius: 6px; }
.ar-compile-ok { margin-top: 8px; font-size: 13px; color: #2e7d32; background: #e8f5e9; padding: 6px 10px; border-radius: 6px; }
/* ── Juste/Faux admin ─────────────────────────────────── */
.jf-admin-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.jf-label-input {
  flex: 1;
}

.jf-toggle-btn {
  min-width: 36px;
  height: 36px;
  border: 2px solid #b0bec5;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  cursor: pointer;
  font-weight: bold;
  color: #607d8b;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.jf-active-vrai {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #2e7d32;
}

.jf-active-faux {
  background: #ffebee;
  border-color: #f44336;
  color: #c62828;
}

.jf-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.6;
  padding: 4px;
}

.jf-remove-btn:hover { opacity: 1; }

/* ── Mots Manquants admin ─────────────────────────────── */
.mm-mode-toggle {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.mm-mode-btn {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid #b0bec5;
  border-radius: 10px;
  background: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: #546e7a;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.mm-mode-active {
  background: #e3f2fd;
  border-color: #1976d2;
  color: #1565c0;
}

.puzzle-thumb {
  width: 160px;
  height: 160px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  border: 2px solid #b0bec5;
}

/* ── Edit form ───────────────────────────────────── */
.edit-form {
  background: #fafafa;
}

.field-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.field-group {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.field-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-icon {
  cursor: pointer;
  font-size: 14px;
  user-select: none;
  opacity: 0.7;
}

.info-icon:hover {
  opacity: 1;
}

.info-box {
  background: #e8f0fe;
  border-left: 3px solid #1a73e8;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  color: #1a3a8f;
  line-height: 1.6;
  margin-bottom: 6px;
}

.create-notice {
  background: #fffbea;
  border: 1px solid #f0d060;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #7a5c00;
}

.coords-row {
  display: flex;
  gap: 8px;
}

.coords-row .form-input {
  flex: 1;
}

.form-input,
.form-input-sm,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border 0.15s;
  background: white;
}

.form-input {
  padding: 12px 14px;
  font-size: 15px;
}

.form-textarea {
  resize: vertical;
}

.form-input:focus,
.form-input-sm:focus,
.form-textarea:focus {
  border-color: #1a73e8;
}

.input-error { border-color: #e53e3e !important; background: #fff5f5 !important; }
.input-error:focus { border-color: #c53030 !important; }

.url-display { display: flex; align-items: center; gap: 10px; background: #f0f4f8; border: 1px solid #ddd; border-radius: 8px; padding: 10px 14px; word-break: break-all; }
.url-text { flex: 1; font-size: 13px; color: #333; }
.btn-copy-url { flex-shrink: 0; padding: 6px 12px; background: #1a73e8; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }
.btn-copy-url:hover { background: #1557b0; }

.correct-input {
  border-color: #a5d6a7;
  background: #f1f8e9;
}

.correct-input:focus {
  border-color: #388e3c;
}

.section-divider {
  border-top: 1px solid #e0e0e0;
  margin: 16px 0 12px;
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  padding-top: 12px;
}

/* ── Answers ─────────────────────────────────────── */
.answer-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.answer {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.answer.correct {
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 600;
}

.answer.wrong {
  background: #fce4ec;
  color: #880e4f;
}

.thumb-row { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
.btn-edit-photo { margin-top: 8px; padding: 6px 14px; border-radius: 8px; border: 1.5px solid #3b82f6; background: #eff6ff; color: #1d4ed8; font-size: 13px; font-weight: 600; cursor: pointer; }
.btn-edit-photo:hover { background: #dbeafe; }
.ar-gif-options { display: flex; gap: 12px; margin-top: 8px; }
.ar-dir-toggle { display: flex; gap: 6px; }
.ar-anim-toggle { display: flex; gap: 6px; margin-top: 4px; }
.ar-dir-btn { flex: 1; padding: 6px 10px; border-radius: 8px; border: 1.5px solid #cbd5e1; background: #f8fafc; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.ar-dir-btn.active { background: #2563eb; border-color: #2563eb; color: #fff; }
.ar-deg-input { display: flex; align-items: center; gap: 6px; }
.ar-deg-unit { font-size: 16px; font-weight: 700; color: #475569; }
.field-hint { font-size: 11px; color: #94a3b8; margin-top: 3px; }

/* ── Mode cards ──────────────────────────────────── */
.mode-cards {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.mode-card {
  flex: 1;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: #f8fafc;
}
.mode-card:hover { border-color: #93c5fd; background: #eff6ff; }
.mode-card.active { border-color: #3b82f6; background: #eff6ff; }
.mode-card-icon { font-size: 24px; margin-bottom: 6px; }
.mode-card-title { font-weight: 700; font-size: 14px; color: #1e40af; margin-bottom: 4px; }
.mode-card.active .mode-card-title { color: #1d4ed8; }
.mode-card-desc { font-size: 12px; color: #64748b; line-height: 1.4; }

/* ── Settings ────────────────────────────────────── */
.settings-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.settings-title {
  font-weight: 700;
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.check-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #444;
  cursor: pointer;
}

.check-label input[type=checkbox] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 4px 0;
}

.order-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.order-index {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  white-space: nowrap;
  min-width: 60px;
}

.help-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
}

.save-notice {
  font-size: 12px;
}

/* ── Buttons ─────────────────────────────────────── */
.btn-primary-full {
  width: 100%;
  padding: 13px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  transition: background 0.15s;
}

.btn-primary-full:hover {
  background: #1557b0;
}

.btn-google {
  width: 100%;
  padding: 13px;
  background: white;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 12px;
  transition: box-shadow 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-google:hover {
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  padding: 9px 18px;
  background: #2e7d32;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-success:hover {
  background: #1b5e20;
}

.btn-success:disabled {
  background: #9e9e9e;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 9px 18px;
  background: #e0e0e0;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: #bdbdbd;
}

.btn-edit {
  padding: 4px 10px;
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.btn-save {
  padding: 4px 10px;
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.btn-cancel {
  padding: 4px 10px;
  background: #fce4ec;
  color: #880e4f;
  border: 1px solid #f48fb1;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.btn-ghost {
  padding: 6px 12px;
  background: transparent;
  color: #1a73e8;
  border: 1.5px dashed #90caf9;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: block;
}

.btn-ghost:hover {
  background: #e8f0fe;
}

.btn-danger-sm {
  padding: 4px 10px;
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.row-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* ── Upload ──────────────────────────────────────── */
.upload-hint {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}
.field-warn { font-size: 12px; color: #e65100; margin-top: 4px; }
.mem-img-preview-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.mem-img-preview { position: relative; width: 60px; height: 60px; }
.mem-img-preview img { width: 60px; height: 60px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; display: block; }
.mem-img-remove { position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; border-radius: 50%; border: none; background: #e53935; color: #fff; font-size: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }

/* ── Spinner ─────────────────────────────────────── */
.spinner-wrap {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Toast ───────────────────────────────────────── */
.toast-msg {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.2s ease;
}

.toast-msg.success {
  background: #2e7d32;
  color: white;
}

.toast-msg.error {
  background: #c62828;
  color: white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
}

.mt-2 {
  margin-top: 8px;
}

.mt-3 {
  margin-top: 12px;
}

.mt-4 {
  margin-top: 16px;
}

.text-muted {
  color: #888;
}

/* ── Multiple match ──────────────────────────────── */
.mm-section {
  margin-top: 12px;
}

.mm-read-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 8px;
}

.mm-pair-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.mm-thumb {
  width: 150px;
  height: 200px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ddd;
  display: block;
}

.mm-empty {
  width: 150px;
  height: 200px;
  background: #f0f0f0;
  border-radius: 6px;
  border: 1px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #aaa;
}

.mm-desc {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  color: #333;
}

.mm-pair-edit {
  margin-bottom: 12px;
  padding: 12px;
  background: #f0f4f8;
  border-radius: 8px;
  border: 1px solid #dde3ef;
}

.mm-pair-header {
  font-size: 12px;
  font-weight: 700;
  color: #1a73e8;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.mm-pair-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.mm-img-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.mm-label-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
}

/* ── Language switcher ── */
.lang-switcher {
  display: flex;
  gap: 4px;
  margin-left: 8px;
}

.lang-btn {
  padding: 4px 10px;
  border-radius: 12px;
  border: 1.5px solid #1a73e8;
  background: white;
  color: #1a73e8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.lang-btn.active {
  background: #1a73e8;
  color: white;
}

.lang-btn:hover:not(.active) {
  background: #e8f0fe;
}

/* ── Map picker ── */
.btn-map-pick {
  background: #e8f0fe;
  border: 1px solid #1a73e8;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 16px;
  cursor: pointer;
  align-self: flex-end;
  flex-shrink: 0;
  transition: background 0.15s;
}

.btn-map-pick:hover {
  background: #c5d8fd;
}

.map-picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 10100;
  display: flex;
  flex-direction: column;
  background: #000;
}

.map-picker-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #1a1a2e;
  color: white;
  padding: 10px 16px;
  font-size: 14px;
  flex-shrink: 0;
}

.map-picker-coords {
  background: rgba(255, 255, 255, 0.15);
  padding: 3px 10px;
  border-radius: 12px;
  font-family: monospace;
  font-size: 13px;
}

.map-picker-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.map-picker-map {
  flex: 1;
  width: 100%;
}

.brightness-slider { width: 100%; accent-color: #f59e0b; }

/* ── Sign Card admin ───────────────────────────────────── */
.sc-admin-preview-wrap {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
}
.sc-admin-img {
  display: block;
  max-width: 100%;
  max-height: 260px;
  border-radius: 8px;
}
.sc-range { width: 100%; accent-color: #2563eb; }
.sc-align-row { display: flex; gap: 6px; }
.sc-align-btn {
  flex: 1;
  padding: 6px 8px;
  border-radius: 7px;
  border: 1.5px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.sc-align-btn.sc-align-active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.admin-lang-switcher { display: flex; gap: 4px; align-items: center; }
.admin-lang-btn { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); border-radius: 6px; padding: 4px 8px; font-size: 16px; cursor: pointer; transition: background 0.15s; }
.admin-lang-btn.admin-lang-active { background: rgba(255,255,255,0.4); border-color: white; }
</style>
